/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useState, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Button } from '@carbon/react';
import { Close } from '@carbon/icons-react';

import * as Styled from './SidePanel.styled';

/**
 * A reusable side panel component that slides in from the right side of the screen.
 * Provides open/close/toggle methods via ref and supports customizable content and styling.
 *
 * @param {Object} props - Component properties
 * @param {string} [props.title] - Title displayed in the panel header
 * @param {any} props.children - Content to display in the panel body
 * @param {boolean} [props.defaultOpen=false] - Whether the panel should be open by default
 * @param {string} [props.width='50%'] - Width of the panel (CSS value: px, %, vw, etc.)
 * @param {string} [props.ariaLabel] - Accessibility label for the panel
 * @param {any} ref - React ref that exposes { open } method
 * @returns {any} The SidePanel component
 *
 * @example
 * // Basic usage with ref
 * const panelRef = useRef();
 *
 * const openPanel = () => {
 *   panelRef.current?.open();
 * };
 *
 * return (
 *   <>
 *     <button onClick={openPanel}>Open Panel</button>
 *     <SidePanel ref={panelRef} title="My Panel" width="60%">
 *       <p>Panel content goes here</p>
 *     </SidePanel>
 *   </>
 * );
 */
const SidePanelComponent = (props, ref) => {
  const { title, children, defaultOpen = false, width = '50%', ariaLabel } = props;

  const [isOpen, setIsOpen] = useState(defaultOpen);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      open
    }),
    [open]
  );

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Styled.Overlay onClick={() => setIsOpen(false)} onKeyDown={handleKeyDown} role="presentation">
      <Styled.Panel
        $width={width}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || title || 'Side Panel'}
        tabIndex={-1}
      >
        <Styled.Header>
          {title && <Styled.Title>{title}</Styled.Title>}
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Close}
            iconDescription="Close panel"
            tooltipPosition="left"
            hasIconOnly
            onClick={() => setIsOpen(false)}
            aria-label="Close panel"
          />
        </Styled.Header>
        <Styled.Content>{children}</Styled.Content>
      </Styled.Panel>
    </Styled.Overlay>
  );
};

/*
 * If we use forward ref in the component definition we are going to have type errors that can't be solved without TypeScript.
 * This is a workaround to avoid those errors. The component is still usable with refs with full documentation and type safety.
 */
export const SidePanel = forwardRef(SidePanelComponent);

// displayName is used to identify the component in React DevTools and for debugging purposes, it will be ignored in production builds.
SidePanel.displayName = 'SidePanel';
